import { useState, useMemo } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ptBR } from "date-fns/locale";
import { differenceInDays, startOfToday, format } from "date-fns";
import { CalendarDays, Moon, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatBRL = (value) =>
  value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });

const formatCPF = (raw) =>
  raw
    .replace(/\D/g, "")
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");

const formatPhone = (raw) => {
  const v = raw.replace(/\D/g, "").slice(0, 11);
  if (v.length <= 10) {
    return v.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3").trim();
  }
  return v.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3").trim();
};

export const BookingFlow = ({ cabana }) => {
  const today = startOfToday();
  const [range, setRange] = useState();
  const [form, setForm] = useState({
    nome_completo: "",
    data_nascimento: "",
    cpf: "",
    email: "",
    telefone: "",
    nome_acompanhante: "",
    numero_hospedes: "2",
    pet: "nao",
    forma_pagamento: "pix",
    parcelas: "1",
    solicitacao_especial: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const nights = useMemo(() => {
    if (!range?.from || !range?.to) return 0;
    return Math.max(0, differenceInDays(range.to, range.from));
  }, [range]);

  const subtotal = nights * cabana.price;
  const petFee = form.pet === "sim" ? 200 : 0;
  const total = subtotal + petFee;

  const update = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const validateAndSubmit = async (e) => {
    e.preventDefault();
    if (!range?.from || !range?.to || nights === 0) {
      toast.error("Selecione as datas de check-in e check-out no calendário.");
      return;
    }
    if (form.nome_completo.trim().length < 3) {
      toast.error("Informe seu nome completo.");
      return;
    }
    if (form.cpf.replace(/\D/g, "").length !== 11) {
      toast.error("CPF inválido.");
      return;
    }
    if (!form.data_nascimento) {
      toast.error("Informe sua data de nascimento.");
      return;
    }
    if (!form.email.includes("@")) {
      toast.error("E-mail inválido.");
      return;
    }
    if (form.telefone.replace(/\D/g, "").length < 10) {
      toast.error("WhatsApp inválido.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        cabana_id: cabana.id,
        cabana_name: cabana.name,
        cabana_price: cabana.price,
        check_in: format(range.from, "yyyy-MM-dd"),
        check_out: format(range.to, "yyyy-MM-dd"),
        nights,
        total,
        nome_completo: form.nome_completo.trim(),
        data_nascimento: form.data_nascimento,
        cpf: form.cpf,
        email: form.email.trim(),
        telefone: form.telefone,
        nome_acompanhante: form.nome_acompanhante.trim(),
        numero_hospedes: parseInt(form.numero_hospedes, 10),
        pet: form.pet === "sim",
        forma_pagamento: form.forma_pagamento,
        parcelas:
          form.forma_pagamento === "cartao" ? parseInt(form.parcelas, 10) : 1,
        solicitacao_especial: form.solicitacao_especial.trim(),
      };
      const { data } = await axios.post(`${API}/reservations`, payload);
      setSuccess(data);
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          "Não foi possível enviar sua reserva. Tente novamente."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="reservar"
      data-testid="booking-flow"
      className="bg-[#F9F6F0] py-20 lg:py-28 border-t border-[#E3DCD2]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <span className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#5C6661] font-bold mb-5">
            <span className="w-8 h-px bg-[#2C5545]" />
            Reserve agora
          </span>
          <h2 className="text-4xl sm:text-5xl font-black text-[#1A2421] tracking-tighter leading-[1.05]">
            Escolha suas datas<br />e finalize sua reserva.
          </h2>
        </div>

        {/* Calendar at the top */}
        <div className="bg-white border border-[#E3DCD2] p-6 lg:p-10 mb-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-6">
                <CalendarDays size={20} className="text-[#2C5545]" strokeWidth={1.6} />
                <h3 className="text-xl font-bold text-[#1A2421]">
                  Selecione check-in e check-out
                </h3>
              </div>
              <div className="flex justify-center lg:justify-start" data-testid="reservation-calendar">
                <Calendar
                  mode="range"
                  selected={range}
                  onSelect={setRange}
                  numberOfMonths={2}
                  locale={ptBR}
                  disabled={{ before: today }}
                  className="bg-[#F9F6F0]"
                />
              </div>
              <p className="text-xs text-[#5C6661] mt-4">
                Check-in a partir das 12h00 · Check-out até as 17h00
              </p>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-[#F2EFE9] p-6 lg:p-8 border border-[#E3DCD2] sticky top-28">
                <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661] font-bold mb-2">
                  Resumo
                </p>
                <h4 className="text-2xl font-bold text-[#1A2421] mb-6">
                  {cabana.name}
                </h4>

                <div className="space-y-3 text-sm pb-6 border-b border-[#E3DCD2]">
                  <div className="flex justify-between" data-testid="summary-checkin">
                    <span className="text-[#5C6661]">Check-in</span>
                    <span className="font-semibold">
                      {range?.from ? format(range.from, "dd/MM/yyyy") : "—"}
                    </span>
                  </div>
                  <div className="flex justify-between" data-testid="summary-checkout">
                    <span className="text-[#5C6661]">Check-out</span>
                    <span className="font-semibold">
                      {range?.to ? format(range.to, "dd/MM/yyyy") : "—"}
                    </span>
                  </div>
                  <div className="flex justify-between" data-testid="summary-nights">
                    <span className="text-[#5C6661] inline-flex items-center gap-1.5">
                      <Moon size={14} strokeWidth={1.6} /> Noites
                    </span>
                    <span className="font-semibold">{nights}</span>
                  </div>
                </div>

                <div className="space-y-3 text-sm py-6 border-b border-[#E3DCD2]">
                  <div className="flex justify-between">
                    <span className="text-[#5C6661]">
                      {formatBRL(cabana.price)} × {nights || 0}{" "}
                      {nights === 1 ? "noite" : "noites"}
                    </span>
                    <span className="font-semibold" data-testid="summary-subtotal">
                      {formatBRL(subtotal)}
                    </span>
                  </div>
                  {petFee > 0 && (
                    <div className="flex justify-between" data-testid="summary-pet-fee">
                      <span className="text-[#5C6661]">Taxa pet (higienização)</span>
                      <span className="font-semibold">{formatBRL(petFee)}</span>
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-baseline pt-6">
                  <span className="text-base font-bold text-[#1A2421] uppercase tracking-wider">
                    Total
                  </span>
                  <span
                    data-testid="summary-total"
                    className="text-3xl font-black text-[#2C5545] tracking-tight"
                  >
                    {formatBRL(total)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={validateAndSubmit}
          data-testid="reservation-form"
          className="bg-white border border-[#E3DCD2] p-6 lg:p-10"
        >
          <h3 className="text-2xl font-bold text-[#1A2421] mb-2">
            Dados da reserva
          </h3>
          <p className="text-sm text-[#5C6661] mb-8">
            Receberemos sua solicitação no WhatsApp e entraremos em contato em
            seguida.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Nome completo">
              <Input
                data-testid="input-nome"
                value={form.nome_completo}
                onChange={update("nome_completo")}
                placeholder="Digite seu nome completo"
                required
                className="h-12 bg-[#F9F6F0] border-[#E3DCD2] rounded-none focus-visible:ring-[#2C5545]"
              />
            </Field>

            <Field label="Data de nascimento">
              <Input
                data-testid="input-nascimento"
                type="date"
                value={form.data_nascimento}
                onChange={update("data_nascimento")}
                required
                className="h-12 bg-[#F9F6F0] border-[#E3DCD2] rounded-none focus-visible:ring-[#2C5545]"
              />
            </Field>

            <Field label="CPF">
              <Input
                data-testid="input-cpf"
                value={form.cpf}
                onChange={(e) =>
                  setForm((f) => ({ ...f, cpf: formatCPF(e.target.value) }))
                }
                placeholder="000.000.000-00"
                required
                className="h-12 bg-[#F9F6F0] border-[#E3DCD2] rounded-none focus-visible:ring-[#2C5545]"
              />
            </Field>

            <Field label="E-mail">
              <Input
                data-testid="input-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="seuemail@exemplo.com"
                required
                className="h-12 bg-[#F9F6F0] border-[#E3DCD2] rounded-none focus-visible:ring-[#2C5545]"
              />
            </Field>

            <Field label="Telefone (WhatsApp)">
              <Input
                data-testid="input-telefone"
                value={form.telefone}
                onChange={(e) =>
                  setForm((f) => ({ ...f, telefone: formatPhone(e.target.value) }))
                }
                placeholder="(41) 90000-0000"
                required
                className="h-12 bg-[#F9F6F0] border-[#E3DCD2] rounded-none focus-visible:ring-[#2C5545]"
              />
            </Field>

            <Field label="Nome do acompanhante">
              <Input
                data-testid="input-acompanhante"
                value={form.nome_acompanhante}
                onChange={update("nome_acompanhante")}
                placeholder="Caso esteja viajando acompanhado"
                className="h-12 bg-[#F9F6F0] border-[#E3DCD2] rounded-none focus-visible:ring-[#2C5545]"
              />
            </Field>

            <Field label="Número de hóspedes">
              <Select
                value={form.numero_hospedes}
                onValueChange={(v) => setForm((f) => ({ ...f, numero_hospedes: v }))}
              >
                <SelectTrigger
                  data-testid="select-hospedes"
                  className="h-12 bg-[#F9F6F0] border-[#E3DCD2] rounded-none focus:ring-[#2C5545]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n} {n === 1 ? "hóspede" : "hóspedes"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Vai levar pet?">
              <RadioGroup
                value={form.pet}
                onValueChange={(v) => setForm((f) => ({ ...f, pet: v }))}
                className="flex gap-6 h-12 items-center"
                data-testid="radio-pet"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="nao" id="pet-nao" data-testid="radio-pet-nao" />
                  <Label htmlFor="pet-nao" className="cursor-pointer">Não</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="sim" id="pet-sim" data-testid="radio-pet-sim" />
                  <Label htmlFor="pet-sim" className="cursor-pointer">
                    Sim (+ R$ 200,00)
                  </Label>
                </div>
              </RadioGroup>
            </Field>

            <Field label="Forma de pagamento">
              <Select
                value={form.forma_pagamento}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, forma_pagamento: v, parcelas: "1" }))
                }
              >
                <SelectTrigger
                  data-testid="select-pagamento"
                  className="h-12 bg-[#F9F6F0] border-[#E3DCD2] rounded-none focus:ring-[#2C5545]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pix">PIX</SelectItem>
                  <SelectItem value="cartao">Cartão de crédito</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            {form.forma_pagamento === "cartao" && (
              <Field label="Parcelamento">
                <Select
                  value={form.parcelas}
                  onValueChange={(v) => setForm((f) => ({ ...f, parcelas: v }))}
                >
                  <SelectTrigger
                    data-testid="select-parcelas"
                    className="h-12 bg-[#F9F6F0] border-[#E3DCD2] rounded-none focus:ring-[#2C5545]"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6].map((p) => (
                      <SelectItem key={p} value={String(p)}>
                        {p}x sem juros · {formatBRL(total / p || 0)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
          </div>

          <div className="mt-6">
            <Label className="text-sm font-bold text-[#1A2421]">
              Alguma solicitação especial?
            </Label>
            <Textarea
              data-testid="input-solicitacao"
              value={form.solicitacao_especial}
              onChange={update("solicitacao_especial")}
              placeholder="Conte-nos qualquer detalhe que possamos preparar para você."
              rows={4}
              className="mt-2 bg-[#F9F6F0] border-[#E3DCD2] rounded-none focus-visible:ring-[#2C5545]"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="submit-reservation"
            className="mt-10 h-14 w-full bg-[#2C5545] text-[#F9F6F0] font-bold text-base uppercase tracking-[0.15em] hover:bg-[#1E3B30] transition-colors flex items-center justify-center gap-3 disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="animate-spin" /> Enviando...
              </>
            ) : (
              <>Solicitar reserva · {formatBRL(total)}</>
            )}
          </button>

          <p className="text-xs text-[#5C6661] text-center mt-4">
            Não realizamos pagamento online. Sua solicitação será enviada para
            nossa equipe via WhatsApp.
          </p>
        </form>
      </div>

      {/* Success dialog */}
      <Dialog open={!!success} onOpenChange={(open) => !open && setSuccess(null)}>
        <DialogContent
          data-testid="success-dialog"
          className="bg-[#F9F6F0] border-[#E3DCD2] rounded-none max-w-lg"
        >
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <span className="w-16 h-16 bg-[#2C5545] text-[#F9F6F0] flex items-center justify-center">
                <CheckCircle2 size={32} strokeWidth={1.5} />
              </span>
            </div>
            <DialogTitle className="text-center text-2xl font-black text-[#1A2421] tracking-tight">
              Sua reserva foi concluída com Sucesso!
            </DialogTitle>
            <DialogDescription className="text-center text-base text-[#5C6661] pt-2">
              Os dados foram enviados para nossa equipe via WhatsApp. Em breve
              entraremos em contato com você para confirmar todos os detalhes.
            </DialogDescription>
          </DialogHeader>

          {success && (
            <div className="bg-white border border-[#E3DCD2] p-5 my-4 text-sm">
              <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661] font-bold mb-2">
                Protocolo
              </p>
              <p className="font-mono text-xs text-[#1A2421] break-all">
                {success.reservation_id}
              </p>
              <p className="mt-4 text-[#5C6661]">
                Total: <span className="font-bold text-[#1A2421]">{formatBRL(success.total)}</span>
              </p>
            </div>
          )}

          <button
            onClick={() => setSuccess(null)}
            data-testid="success-close"
            className="h-12 w-full bg-[#1A2421] text-[#F9F6F0] font-bold uppercase tracking-[0.15em] text-sm hover:bg-[#2C5545] transition-colors"
          >
            Fechar
          </button>
        </DialogContent>
      </Dialog>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-2">
    <Label className="text-sm font-bold text-[#1A2421]">{label}</Label>
    {children}
  </div>
);

export default BookingFlow;
